import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import validator from 'validator'

import { getPasswordValidationError } from '../../../utils/passwordUtils'

import './OTPForm.css'
import CButton from '../../../components/UI/CButton'
import CTextField from '../../../components/UI/CTextField'

/**
 * OTP and password form component
 * Used for first-time login and password reset flows
 */
const OTPForm = ({
    onSubmit,
    onResendOTP,
    user,
    title = "Set up your account",
    subtitle = null,
    submitLabel = "Complete Setup"
}) => {
    const { isLoading } = useSelector((state) => state.auth)

    const [otp, setOtp] = useState(['', '', '', '', '', ''])
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordError, setPasswordError] = useState('')
    const [confirmPasswordError, setConfirmPasswordError] = useState('')
    const [countdown, setCountdown] = useState(300)
    const [canResend, setCanResend] = useState(false)
    const [showPasswords, setShowPasswords] = useState({
        password: false,
        confirmPassword: false,
    })
    const inputRefs = useRef([])

    const contactInfo = user?.cellphonenumber || user?.emailaddress || 'your registered contact'

    // Countdown timer
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1)
            }, 1000)
            return () => clearTimeout(timer)
        } else if (countdown === 0) {
            setCanResend(true)
        }
    }, [countdown])

    // Focus first OTP input on mount
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus()
        }
    }, [])

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const handleOtpChange = (index, value) => {
        if (value && !validator.isNumeric(value)) {
            return
        }

        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus()
        }
    }

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus()
        }
    }

    const handlePaste = (e) => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text/plain').trim()

        if (validator.isNumeric(pastedData) && pastedData.length === 6) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
            inputRefs.current[5]?.focus()
        }
    }

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
    }

    const handlePasswordChange = (e) => {
        const val = e.target.value
        setPassword(val)
        setPasswordError(getPasswordValidationError(val))
        if (confirmPassword) {
            setConfirmPasswordError(val !== confirmPassword ? 'Passwords do not match' : '')
        }
    }

    const handleConfirmPasswordChange = (e) => {
        const val = e.target.value
        setConfirmPassword(val)
        setConfirmPasswordError(val && password !== val ? 'Passwords do not match' : '')
    }

    const handleResendClick = async () => {
        if (!canResend) return

        if (onResendOTP) {
            const success = await onResendOTP()
            if (success) {
                setOtp(['', '', '', '', '', ''])
                inputRefs.current[0]?.focus()
                setCountdown(300)
                setCanResend(false)
            }
        }
    }

    const validateForm = () => {
        const otpValue = otp.join('')
        let hasError = false

        if (otpValue.length !== 6) {
            setPasswordError('Please enter the complete 6-digit OTP code')
            return false
        }

        if (validator.isEmpty(password.trim())) {
            setPasswordError('Password is required')
            hasError = true
        } else if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters')
            hasError = true
        } else {
            // Check for uppercase letter
            if (!/[A-Z]/.test(password)) {
                setPasswordError('Password must include at least one uppercase letter')
                hasError = true
            }
            // Check for lowercase letter
            else if (!/[a-z]/.test(password)) {
                setPasswordError('Password must include at least one lowercase letter')
                hasError = true
            }
            // Check for number
            else if (!/[0-9]/.test(password)) {
                setPasswordError('Password must include at least one number')
                hasError = true
            }
            // Check for special character
            else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
                setPasswordError('Password must include at least one special character')
                hasError = true
            } else {
                setPasswordError('')
            }
        }

        if (validator.isEmpty(confirmPassword.trim())) {
            setConfirmPasswordError('Please confirm your password')
            hasError = true
        } else if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match')
            hasError = true
        } else {
            setConfirmPasswordError('')
        }

        return !hasError
    }

    const isFormValid = () => {
        const otpValue = otp.join('')

        // Check OTP is complete
        if (otpValue.length !== 6) {
            return false
        }

        // Check if password fields have values
        if (!password.trim() || !confirmPassword.trim()) {
            return false
        }

        // Check password requirements
        if (password.length < 8) {
            return false
        }

        if (!/[A-Z]/.test(password)) {
            return false
        }

        if (!/[a-z]/.test(password)) {
            return false
        }

        if (!/[0-9]/.test(password)) {
            return false
        }

        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return false
        }

        // Check passwords match
        if (password !== confirmPassword) {
            return false
        }

        return true
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        const otpValue = otp.join('')
        if (onSubmit) {
            onSubmit({ otp: otpValue, password })
        }
    }

    const defaultSubtitle = user?.firstnames
        ? `Welcome, ${user.firstnames}! Enter the OTP sent to ${contactInfo} and create a secure password.`
        : `Enter the OTP sent to ${contactInfo} and create a new password.`

    return (
        <>
            <div className="form-header">
                <h2 className="form-title header-2">{title}</h2>
                <p className="form-subtitle text-4">
                    {subtitle || defaultSubtitle}
                </p>
            </div>

            <div className="otp-inputs-container">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        ref={el => inputRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        className="otp-input"
                        disabled={isLoading}
                        aria-label={`Digit ${index + 1}`}
                    />
                ))}
            </div>

            <div className="otp-resend-container">
                <p className="otp-resend-text text-5">
                    {canResend ? (
                        "Didn't get the OTP? You can resend it now."
                    ) : (
                        <>
                            Didn't get the OTP? You can resend it in{' '}
                            <span className="countdown-timer">{formatTime(countdown)}</span>
                        </>
                    )}
                </p>
            </div>

            <CButton
                className="resend-button-compact"
                type="button"
                variant="text"
                size="large"
                fullWidth
                onClick={handleResendClick}
                disabled={isLoading || !canResend}
                colorTheme="secondary"
                label="Resend OTP"
            />

            <div className="divider"></div>

            <div className="password-fields">
                <CTextField
                    fullWidth
                    label="New Password"
                    name="password"
                    type={showPasswords.password ? "text" : "password"}
                    leftIcon="lock"
                    rightIcon={showPasswords.password ? "hide-password" : "reveal-password"}
                    onRightIconClick={() => togglePasswordVisibility('password')}
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!passwordError}
                    helperText={passwordError}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                />

                <CTextField
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showPasswords.confirmPassword ? "text" : "password"}
                    leftIcon="lock"
                    rightIcon={showPasswords.confirmPassword ? "hide-password" : "reveal-password"}
                    onRightIconClick={() => togglePasswordVisibility('confirmPassword')}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    error={!!confirmPasswordError}
                    helperText={confirmPasswordError}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                />
            </div>

            <div className="password-requirements">
                <span className="header-5 title">Password requirements:</span>
                <ul>
                    <li className="text-5">Password must be at least 8 characters</li>
                    <li className="text-5">Passwords must have at least one uppercase letter, one lowercase letter, one number, and one special character</li>
                </ul>
            </div>

            <CButton
                className="submit-button"
                type="button"
                variant="contained"
                size="large"
                fullWidth
                showLoader={isLoading}
                onClick={handleSubmit}
                colorTheme="primary"
                label={submitLabel}
                disabled={isLoading || !isFormValid()}
            />
        </>
    )
}

OTPForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onResendOTP: PropTypes.func,
    user: PropTypes.object,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    submitLabel: PropTypes.string,
}

export default OTPForm
